var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useEffect, useMemo, useState } from 'react';
import { makeAutoObservable, runInAction } from 'mobx';
import Ajv from 'ajv';
import addErrors from 'ajv-errors';
import addFormats from 'ajv-formats';
import * as EmailValidator from 'email-validator';
import _ from 'lodash';
import { isoToObject } from 'ziphy-web-shared/basic/api/myack/api.helpers';
import { $definitions } from 'ziphy-web-shared/basic/entities/notes/definitions';
import { getUuid, isAbsoluteEmpty, isBase64, isJson, isLooselyEqual, joinHeightToInches, saveImageToBase64, splitBloodPressure, splitHeightFromInches, toNumberIfPossible, } from 'ziphy-web-shared/basic/helpers';
import { defineAjvCustomKeywords } from './Form.ajv';
export const ajv = new Ajv({ allErrors: true, $data: true });
defineAjvCustomKeywords(ajv);
addFormats(ajv);
addErrors(ajv);
export class FieldStore {
    constructor(props) {
        this.startedValidators = {};
        this.id = getUuid();
        this.changeTime = null;
        this.isChanged = false;
        this.isTouched = false;
        this.isValid = true;
        this.isInvalid = false;
        this.error = '';
        this.onChange = (newValue, useIsChanged = true) => {
            runInAction(() => {
                this.value = this.transform('change', newValue);
                if (useIsChanged) {
                    this.changeTime = new Date().toISOString();
                    this.isChanged = !_.isEqual(this.value, this.initialValue);
                    this.isTouched = true;
                }
                this.setError('');
                this.formStore.setError('');
                if (!this.formStore.isSubmitted)
                    return;
                if (!this.startedValidators[this.name]) {
                    this.startedValidators[this.name] = _.debounce(() => this.runValidation(), this.formStore.__private__.debounceValidation);
                }
                this.startedValidators[this.name](this.value);
            });
        };
        this._initProps = props;
        this.formStore = props.formStore;
        this.name = props.name;
        this._config = props.config || {};
        this.initialValue = this.transform('in', props.initialValue);
        this.value = this.initialValue;
        makeAutoObservable(this);
    }
    get config() {
        return Object.assign(Object.assign({}, this.formStore.__private__.fieldsConfig[this.name]), this._config);
    }
    get primaryValue() {
        return this._initProps.initialValue;
    }
    SET_CHANGE_TIME(value) {
        this.changeTime = value;
    }
    // todo: change to getter, needed TS >= 4.9
    isArrayStore() {
        return (Array.isArray(this.value) &&
            this.value.length > 0 &&
            (this.value[0] instanceof FieldStore || this.value[0] instanceof FormStore));
    }
    transform(type, newValue) {
        var _a, _b, _c;
        if (type === 'out') {
            newValue = this.value;
        }
        let config;
        if (type === 'in') {
            config = (_a = this.config) === null || _a === void 0 ? void 0 : _a.transformIn;
        }
        else if (type === 'change') {
            config = (_b = this.config) === null || _b === void 0 ? void 0 : _b.transformOnChange;
        }
        else if (type === 'out') {
            config = (_c = this.config) === null || _c === void 0 ? void 0 : _c.transformOut;
        }
        if (config) {
            return config.reduce((acc, fn) => {
                let isStrict = false;
                if (typeof fn === 'string') {
                    isStrict = fn.endsWith('|strict');
                    if (isStrict)
                        fn = fn.replace(/\|strict$/, '');
                }
                const applyFn = (val) => {
                    if (typeof fn === 'function')
                        return fn(val, this);
                    if (typeof fn === 'string') {
                        const transformFn = defaultTransformations[fn];
                        return transformFn ? transformFn(val, this) : val;
                    }
                    return val;
                };
                if (!isStrict && Array.isArray(acc))
                    return acc.map(applyFn);
                return applyFn(acc);
            }, newValue);
        }
        return newValue;
    }
    outputValues(options = {}) {
        const mapFieldValue = (store) => {
            if (store instanceof FormStore) {
                return store.outputValues(options);
            }
            else {
                const value = store.transform('out');
                return options.withChangeTime ? { value: value, datetime: store.changeTime } : value;
            }
        };
        return this.isArrayStore()
            ? this.value
                .filter((store) => !options.onlyChanged || store.isChanged)
                .map(mapFieldValue)
                .filter((x) => x !== null)
            : mapFieldValue(this);
    }
    validate() {
        let promises = [];
        if (this.isArrayStore()) {
            this.value.forEach((store) => {
                promises = [...promises, ...store.validate()];
            });
        }
        else {
            promises.push(this.runValidation());
        }
        return promises;
    }
    runValidation() {
        return __awaiter(this, void 0, void 0, function* () {
            const formValues = this.formStore.outputValues();
            const processedValue = this.outputValues();
            let error = '';
            if (this.config.validate) {
                const fieldValidate = this.config.validate;
                let validators = typeof fieldValidate === 'function'
                    ? fieldValidate(processedValue, formValues)
                    : fieldValidate;
                for (const validator of validators) {
                    let result;
                    if (typeof validator === 'function') {
                        result = validator(processedValue, formValues);
                    }
                    else if (typeof validator === 'string') {
                        const fn = defaultValidations[validator];
                        result = fn ? fn(processedValue, formValues) : '';
                    }
                    if (result instanceof Promise) {
                        result = yield result;
                    }
                    if (result) {
                        error = result;
                        break;
                    }
                }
            }
            if (!error && this.formStore.__private__.ajvValidator) {
                const valid = this.formStore.__private__.ajvValidator(Object.assign(Object.assign({}, formValues), _.set({}, this.name, processedValue)));
                if (!valid && this.formStore.__private__.ajvValidator.errors) {
                    for (const err of this.formStore.__private__.ajvValidator.errors) {
                        let fieldPath = err.instancePath;
                        if (fieldPath.startsWith('/'))
                            fieldPath = fieldPath.slice(1);
                        fieldPath = fieldPath.replace(/\//g, '.');
                        if (fieldPath.startsWith(this.name)) {
                            error = err.message || '';
                            break;
                        }
                    }
                }
            }
            this.setError(error);
            return Boolean(error);
        });
    }
    setError(error) {
        this.error = error;
        this.isValid = !error;
        this.isInvalid = !!error;
    }
    reset() {
        this.value = this.initialValue;
        this.changeTime = null;
        this.isChanged = false;
        this.isTouched = false;
        this.isValid = true;
        this.isInvalid = false;
        this.error = '';
    }
    toJSON() {
        return {
            name: this.name,
            value: this.value,
            initialValue: this.initialValue,
            changeTime: this.changeTime,
            isChanged: this.isChanged,
            isTouched: this.isTouched,
            isValid: this.isValid,
            isInvalid: this.isInvalid,
            error: this.error,
        };
    }
}
class FormStore {
    constructor(initialValues = {}, options) {
        this.fields = {};
        this.isSubmitted = false;
        this.error = '';
        this._hasError = false;
        this.initialValues = initialValues;
        this.updateOptions(options);
        makeAutoObservable(this);
    }
    updateOptions(options = {}) {
        var _a, _b;
        this.name = options.name;
        this.id = (_a = options.id) !== null && _a !== void 0 ? _a : getUuid();
        this.fieldsConfig = (options.fieldsConfig || {});
        this.debounceValidation = (_b = options.debounceValidation) !== null && _b !== void 0 ? _b : 200;
        this.ajvSchema = options.ajvSchema;
        this.validators = options.validators;
    }
    updateAjvSchema(schema) {
        this.ajvSchema = schema;
    }
    get __private__() {
        return {
            fields: this.fields,
            fieldsConfig: this.fieldsConfig,
            debounceValidation: this.debounceValidation,
            ajvSchema: this.ajvSchema,
            ajvValidator: this.ajvValidator,
        };
    }
    get hasError() {
        return this._hasError || !!this.error;
    }
    get isChanged() {
        return Object.values(this.fields).some((x) => x.isChanged);
    }
    outputValues(options = {}) {
        const result = options.onlyChanged ? {} : _.cloneDeep(this.initialValues);
        return Object.entries(this.fields).reduce((acc, [key, field]) => {
            let value = field.outputValues(options);
            if (!options.onlyChanged || field.isChanged) {
                _.set(acc, key, value);
            }
            return acc;
        }, result);
    }
    field(name, defaultValue, config) {
        if (!this.fields[name]) {
            runInAction(() => {
                this.fields[name] = new FieldStore({
                    formStore: this,
                    name,
                    config,
                    initialValue: _.get(this.initialValues, name, defaultValue),
                });
            });
        }
        return this.fields[name];
    }
    group(name, defaultItemValue, options = {}) {
        const createStore = (value, index = 0) => {
            if (options.type === 'field') {
                return new FieldStore({
                    formStore: this,
                    name: `${name}.${index}`,
                    initialValue: value,
                    config: options.fieldConfig,
                });
            }
            return new FormStore(value, {
                name: `${name}.${index}`,
                debounceValidation: this.debounceValidation,
                ajvSchema: _.get(this.ajvSchema, `properties.${name}.items`),
                fieldsConfig: options.formFieldsConfig,
            });
        };
        const group = this.field(name, [], options.config);
        if (Array.isArray(group.value) && group.value.length > 0 && !group.isArrayStore()) {
            const fieldStores = group.value.map((item, index) => {
                return createStore(item, index);
            });
            group.onChange(fieldStores, false);
        }
        const items = group.value;
        return Object.assign(Object.assign({}, group), { value: items, initialValue: group.initialValue, add: (value = defaultItemValue, toEnd = true) => {
                if (toEnd) {
                    group.onChange([...items, createStore(value, items.length)]);
                }
                else {
                    group.onChange([createStore(value, items.length), ...items]);
                }
            }, remove: (index) => {
                runInAction(() => {
                    if (index < 0 || index >= items.length)
                        return;
                    const newItems = items
                        .filter((_, i) => i !== index)
                        .map((item, i) => {
                        if (item instanceof FieldStore) {
                            item.name = `${name}.${i}`;
                        }
                        return item;
                    });
                    group.onChange(newItems);
                });
            } });
    }
    validate() {
        let promises = [];
        Object.values(this.fields).forEach((field) => {
            promises = [...promises, ...field.validate()];
        });
        return promises;
    }
    validateForm() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.validators) {
                for (const validator of this.validators) {
                    const formValues = this.outputValues();
                    let result;
                    if (typeof validator === 'function') {
                        result = validator(formValues);
                    }
                    else if (typeof validator === 'string') {
                        const fn = formValidations[validator];
                        result = fn ? fn(formValues) : '';
                    }
                    if (result instanceof Promise) {
                        result = yield result;
                    }
                    this.setError(result);
                }
            }
        });
    }
    submit(callback, options = {}) {
        return (e) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            (_a = e === null || e === void 0 ? void 0 : e.preventDefault) === null || _a === void 0 ? void 0 : _a.call(e);
            const results = yield Promise.all(this.validate());
            runInAction(() => {
                this._hasError = results.some((x) => x);
                this.isSubmitted = true;
            });
            if (!this._hasError) {
                yield this.validateForm();
            }
            if (!this._hasError) {
                yield callback(this.outputValues(options));
            }
        });
    }
    reset() {
        runInAction(() => {
            Object.values(this.fields).forEach((value) => value.reset());
            this.setError('');
            this.isSubmitted = false;
        });
    }
    setError(error) {
        this.error = error;
        this._hasError = !isAbsoluteEmpty(error);
    }
    get ajvValidator() {
        return this.ajvSchema ? ajv.compile(this.ajvSchema) : undefined;
    }
}
export function useMobxForm(_a = {}) {
    var { initialValues: values = {}, reinitialize = true } = _a, options = __rest(_a, ["initialValues", "reinitialize"]);
    const [initialValues, setInitialValues] = useState(values);
    useEffect(() => {
        if (reinitialize && !_.isEqual(initialValues, values)) {
            setInitialValues(values);
        }
    }, [values, reinitialize]);
    return useMemo(() => new FormStore(initialValues, options), [initialValues]);
}
export const formValidations = {
    atLeastOne(formValues) {
        return isAbsoluteEmpty(formValues) ||
            (_.isPlainObject(formValues) && Object.values(formValues).every((x) => isAbsoluteEmpty(x)))
            ? 'Please fill in at least one field before submitting'
            : '';
    },
};
export const defaultValidations = {
    required(value) {
        return isAbsoluteEmpty(value) ? 'This field is required' : '';
    },
    requiredCode(value) {
        return isAbsoluteEmpty(value === null || value === void 0 ? void 0 : value.code) && isAbsoluteEmpty(value === null || value === void 0 ? void 0 : value.text)
            ? 'This field is required'
            : '';
    },
    forceNotRequired(value) {
        return '';
    },
    email(value) {
        return !EmailValidator.validate(value) ? 'Invalid email' : '';
    },
    minLen1OrEmpty(value) {
        return value.trim().length >= 1 || value === '' ? '' : 'This field is required';
    },
    bloodPressure(value) {
        if (typeof (value === null || value === void 0 ? void 0 : value.systolic) === 'undefined' || typeof (value === null || value === void 0 ? void 0 : value.diastolic) === 'undefined')
            return '';
        const systolic = isAbsoluteEmpty(value === null || value === void 0 ? void 0 : value.systolic);
        const diastolic = isAbsoluteEmpty(value === null || value === void 0 ? void 0 : value.diastolic);
        if ((!systolic && diastolic) || (systolic && !diastolic)) {
            return 'Fill in the value completely';
        }
        return '';
    },
};
export const defaultTransformations = {
    trim(value) {
        return value.trim();
    },
    lowerCase(value) {
        return value.toLowerCase();
    },
    upperCase(value) {
        return value.toUpperCase();
    },
    integer(value) {
        return parseInt(value);
    },
    toNumber(value) {
        return _.cloneDeepWith(value, (val) => {
            if (_.isPlainObject(val) || Array.isArray(val))
                return undefined;
            return toNumberIfPossible(val);
        });
    },
    string(value) {
        return String(value);
    },
    date(value) {
        return isoToObject('date', value);
    },
    datetime(value) {
        return isoToObject('datetime', value);
    },
    avatar(value) {
        if (isAbsoluteEmpty(value) || value.includes('http://') || value.includes('https://')) {
            return undefined;
        }
        return isBase64(value) ? value.split(';')[1].substring(7) : value;
    },
    emptyUndefined(value) {
        return isAbsoluteEmpty(value) ? undefined : value;
    },
    emptyNull(value) {
        return isAbsoluteEmpty(value) ? null : value;
    },
    phone(value) {
        if (!(value === null || value === void 0 ? void 0 : value.number))
            return null;
        return '+' + value.dialCode + value.number;
    },
    strict(value) {
        return isAbsoluteEmpty(value) ? value : `${value}|strict`;
    },
    parseJson(value) {
        return isJson(value) ? JSON.parse(value) : value;
    },
    bloodPressure(value) {
        return splitBloodPressure(value);
    },
    valueOrEmptyString(value) {
        return !isAbsoluteEmpty(value) ? value : '';
    },
    singleSelect(value) {
        return value[0];
    },
    getImage(value) {
        const file = _.get(value, 'target.files[0]');
        if (file) {
            return saveImageToBase64(file);
        }
    },
    // ----
    dateSelectIn(value) {
        return { value, isFull: false };
    },
    dateSelectOut(value) {
        var _a;
        return (_a = value === null || value === void 0 ? void 0 : value.value) !== null && _a !== void 0 ? _a : '';
    },
    // ----
    fromCodeTextToOption(value) {
        if ((value === null || value === void 0 ? void 0 : value.text) && !value.code)
            return { other: value.text };
        return (value === null || value === void 0 ? void 0 : value.code) ? { [value.code]: true } : {};
    },
    fromOptionToCodeText(value) {
        if (!_.isPlainObject(value) || _.isEmpty(value))
            return {};
        const [code, text] = Object.entries(value)[0];
        return text === true ? { code } : { text };
    },
    // ----
    withDatetimeIn(value) {
        if (_.isPlainObject(value)) {
            return Object.assign(Object.assign({}, value), { datetime: value.datetime || '' });
        }
        return { value, datetime: '' };
    },
    withDatetimeChange(value, field) {
        const isEqual = isLooselyEqual(value, field.value);
        return Object.assign(Object.assign({}, value), { datetime: isEqual ? value.datetime : new Date().toISOString() });
    },
    // ----
    singleHeightIn(value) {
        if (_.isPlainObject(value)) {
            return Object.assign(Object.assign({}, value), splitHeightFromInches(value.value, false));
        }
        return Object.assign({ value }, splitHeightFromInches(isAbsoluteEmpty(value) ? undefined : value, false));
    },
    singleHeightOut(value) {
        let result = joinHeightToInches(value.foot, value.inch);
        if (value.foot === '' && value.inch === '')
            result = '';
        const values = _.omit(value, ['foot', 'inch']);
        if (Object.keys(values).length)
            return Object.assign(Object.assign({}, values), { value: result });
        return result;
    },
    // ----
    optionValueDatetimeIn(value) {
        if (!_.isPlainObject(value) || _.isEmpty(value))
            return {};
        return { [value.value]: value.datetime };
    },
    optionValueDatetimeOut(value, field) {
        if (!_.isPlainObject(value) || _.isEmpty(value))
            return null;
        const [key, datetime] = Object.entries(value)[0];
        const result = { value: key, datetime: datetime };
        const primaryValue = field.primaryValue || {};
        for (const [key, value] of Object.entries(primaryValue)) {
            if (key !== 'value' && key !== 'datetime') {
                result[key] = value;
            }
        }
        return result;
    },
    // ---
    optionStringValueWithOtherIn(value, field) {
        const options = $definitions.selects.items[field.config.definitionSelectId];
        const isSelected = options === null || options === void 0 ? void 0 : options.values.slice(0, options.preferred || undefined).some((item) => item.code === value);
        if (isSelected) {
            return { [value]: true };
        }
        else if (value) {
            return { other: value };
        }
        return {};
    },
    optionStringValueWithOtherOut(value) {
        const [key, objValue] = Object.entries(value)[0] || [];
        if (key === 'other') {
            return objValue;
        }
        else if (key) {
            return key;
        }
        return null;
    },
    // ---
    optionValueWithOtherIn(value, field) {
        const options = $definitions.selects.items[field.config.definitionSelectId];
        const isSelected = options === null || options === void 0 ? void 0 : options.values.slice(0, options.preferred || undefined).some((item) => item.code === value.value);
        if (isSelected) {
            return { [value.value]: true };
        }
        else if (value.value) {
            return { other: value.value };
        }
        return {};
    },
    optionValueWithOtherChange(value) {
        const [key, objValue] = Object.entries(value)[0] || [];
        if (key === 'other') {
            return { other: objValue };
        }
        else if (key) {
            return { [key]: true };
        }
        return {};
    },
    optionValueWithOtherOut(value, field) {
        const primaryValue = field.primaryValue;
        const result = {};
        const [key, objValue] = Object.entries(value)[0] || [];
        if (key === 'other') {
            result['value'] = objValue;
        }
        else if (key) {
            result['value'] = key;
        }
        if (typeof primaryValue === 'object') {
            Object.entries(primaryValue).forEach(([key, value]) => {
                if (key !== 'value') {
                    result[key] = value;
                }
            });
        }
        return result;
    },
    // ---
    optionMultiStringArrayInputIn(value, field) {
        var _a;
        const options = (_a = $definitions.selects.items[field.config.definitionSelectId]) === null || _a === void 0 ? void 0 : _a.values.map((item) => item.code);
        const result = {};
        const valueArray = Array.isArray(value) ? value : [];
        valueArray.forEach((key) => {
            if (options.includes(key)) {
                result[key] = true;
            }
            else {
                result['other'] = key;
            }
        });
        return result;
    },
    optionMultiStringArrayInputOut(value) {
        const result = [];
        const valueKeys = Object.keys(value);
        valueKeys.forEach((key) => {
            if (key === 'other') {
                result.push(value[key]);
            }
            else {
                result.push(key);
            }
        });
        return result;
    },
    // ---
    optionMultiValueInputIn(value) {
        const result = {};
        const valueKeys = Object.keys(value);
        valueKeys.forEach((key) => {
            result[key] = value[key].value;
        });
        return result;
    },
    optionMultiValueInputChange(value) {
        return value;
    },
    optionMultiValueInputOut(value, field) {
        const primaryValue = field.primaryValue;
        const result = {};
        const valueKeys = Object.keys(value);
        valueKeys.forEach((key) => {
            result[key] = { value: value[key] };
            if (typeof primaryValue === 'object' && typeof primaryValue[key] === 'object') {
                Object.entries(primaryValue[key]).forEach(([propKey, value]) => {
                    if (propKey !== 'value') {
                        result[key][propKey] = value;
                    }
                });
            }
        });
        return result;
    },
    // ----
    optionsValueDatetimeIn(value) {
        let result = {};
        value === null || value === void 0 ? void 0 : value.forEach((x) => (result[x.value] = x.datetime));
        return result;
    },
    optionsValueDatetimeChange(value) {
        const prepared = Object.entries(value).map(([key, value]) => {
            if (value === true)
                return [key, new Date().toISOString()];
            return [key, value];
        });
        return Object.fromEntries(prepared);
    },
    optionsValueDatetimeOut(value, field) {
        const primaryValue = field.primaryValue || [];
        const result = [];
        Object.entries(value).forEach(([key, value]) => {
            const item = { value: key, datetime: value };
            const primaryItem = primaryValue.find((item) => item.value === key);
            if (typeof primaryItem === 'object') {
                for (const [key, value] of Object.entries(primaryItem)) {
                    if (key !== 'value' && key !== 'datetime') {
                        item[key] = value;
                    }
                }
            }
            result.push(item);
        });
        return result;
    },
    vitalsEmptyNull(value) {
        let isEmpty = false;
        if (_.isPlainObject(value)) {
            if ('value' in value && isAbsoluteEmpty(value.value))
                isEmpty = true;
            if ('systolic' in value &&
                isAbsoluteEmpty(value.systolic) &&
                'diastolic' in value &&
                isAbsoluteEmpty(value.diastolic)) {
                isEmpty = true;
            }
        }
        else {
            isEmpty = isAbsoluteEmpty(value);
        }
        return isEmpty ? null : value;
    },
};
